// ─── Repeat suppression for tracking events ───────────────────────────────────
//
// _app.js re-keys the page on every completed route change
// (<Component key={pageKey}>), so pages remount and every mount effect runs a
// second time. PostHog and DiagnosisFlowEvent each had their own fix for that:
// drop an event whose exact twin was sent in the last two seconds.
//
// That window was also throwing away real data. Going back a step and forward
// again takes a patient well under two seconds, and the second arrival looks
// identical to a double mount, so it was silently dropped — measured on the
// sign-up steps: with 300ms between back and forward, signup_eligibility_check
// and signup_location_check each arrived once instead of twice; with 2600ms,
// both arrived twice as they should.
//
// What actually tells the two apart is not elapsed time but input. A double
// mount fires the same event twice with no user action in between; a patient
// returning to a step has to click or type to get there. So a repeat is only
// dropped when nothing was touched since the previous one went out.
// ─────────────────────────────────────────────────────────────────────────────

// Only has to cover a remount, which happens in the same route change as the
// first fire. Kept well clear of that rather than tuned to it, since the
// interaction check is what does the real work.
const REPEAT_WINDOW_MS = 2000;

let lastInteractionAt = 0;
let listening = false;

/**
 * Starts watching for user input. Called lazily so importing this module has
 * no side effects, and safe to call repeatedly.
 *
 * Capture phase and passive: this must see the event before any handler can
 * stop it propagating, and must never delay a tap.
 */
function listen() {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  const note = () => {
    lastInteractionAt = Date.now();
  };
  for (const type of ['pointerdown', 'mousedown', 'touchstart', 'keydown']) {
    try {
      window.addEventListener(type, note, { capture: true, passive: true });
    } catch {
      // Older browsers reject the options object; the listener matters more
      // than the options do.
      try {
        window.addEventListener(type, note, true);
      } catch {
        /* give up on this one */
      }
    }
  }
}

/**
 * One suppressor. Each tracking layer keeps its own, so the two cannot
 * interfere with each other's signatures.
 *
 * Returns a function that takes an event signature and answers whether it is
 * a duplicate that should be dropped.
 */
export function createRepeatGuard(windowMs = REPEAT_WINDOW_MS) {
  // One timestamp per signature, not a single slot. A single slot only caught
  // duplicates that happened to be adjacent: a remount fires its event, a
  // pageview fires next, and the remount's twin then looked new again because
  // the pageview had taken the slot. Pruned on write, so the map stays the
  // size of whatever fired in the last couple of seconds.
  const lastSent = new Map();

  return function isRepeat(sig) {
    listen();
    const now = Date.now();
    const seen = lastSent.get(sig);

    if (
      seen !== undefined &&
      now - seen < windowMs &&
      // Nothing was clicked or typed since the twin went out, so this is a
      // remount rather than the patient coming back.
      lastInteractionAt <= seen
    ) {
      return true;
    }

    for (const [key, at] of lastSent) {
      if (now - at >= windowMs) lastSent.delete(key);
    }
    lastSent.set(sig, now);
    return false;
  };
}
